import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useMemo, useState, useEffect } from "react"
import Loader from "../../../components/Loader"
import {
  useMissingPathologyLogByLogId,
  useMissingPathologyLogsByMissingPathology,
  useDistinctBodyParts,
} from "../../../hooks/useMissingPathologyLogs"
import { faChevronDown } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

const columnHelper = createColumnHelper()

const MissingPathologyStatsTable = ({
  data,
  isLoading,
  sorting,
  setSorting,
  setFilters,
  filters,
}) => {
  const [selectedMissingPathologyLogId, setSelectedMissingPathologyLogId] =
    useState(null)
  const [selectedLog, setSelectedLog] = useState(null)

  const { data: distinctBodyParts } = useDistinctBodyParts()

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("Pathology", {
          header: "Pathology",
          id: "Pathology",
          size: 50,
          cell: (info) => info.renderValue() || "",
          enableSorting: true,
          meta: {
            filterVariant: "search",
          },
        }),
        columnHelper.accessor("count", {
          header: "Count",
          id: "count",
          size: 50,
          cell: (info) => info.renderValue() || 0,
          sortDescFirst: true,
          enableColumnFilter: false,
        }),
      ].filter(Boolean),
    []
  )

  const {
    data: missingPathologyLogsData,
    isLoading: isLoadingMissingPathologyLogs,
  } = useMissingPathologyLogsByMissingPathology({
    page: 1,
    limit: 10,
    sortBy: "",
    filters: {
      Pathology: selectedLog ? selectedLog.Pathology : null,
      BodyPart: selectedLog ? selectedLog.BodyPart : null,
    },
  })

  const { data: missingPathologyLog, isLoading: isLoadingMissingPathologyLog } =
    useMissingPathologyLogByLogId(selectedMissingPathologyLogId)

  // Auto-select first row when data loads
  useEffect(() => {
    if (!isLoading && data?.stats && data.stats.length > 0 && !selectedLog) {
      setSelectedLog(data.stats[0])
    }
  }, [isLoading, data?.stats, selectedLog])

  // Reset selections when body part filter changes
  useEffect(() => {
    setSelectedLog(null)
    setSelectedMissingPathologyLogId(null)
  }, [filters])

  // Reset report selection when selectedLog changes
  useEffect(() => {
    setSelectedMissingPathologyLogId(null)
  }, [selectedLog])

  // Auto-select first accession number when missing pathology logs data loads
  useEffect(() => {
    if (
      !isLoadingMissingPathologyLogs &&
      missingPathologyLogsData &&
      missingPathologyLogsData.length > 0 &&
      !selectedMissingPathologyLogId
    ) {
      setSelectedMissingPathologyLogId(
        missingPathologyLogsData[0].MissingPathologiesLogId
      )
    }
  }, [
    isLoadingMissingPathologyLogs,
    missingPathologyLogsData,
    selectedMissingPathologyLogId,
  ])

  const table = useReactTable({
    columns,
    data: data?.stats || [],
    manualSorting: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onColumnFiltersChange: setFilters,
    state: {
      sorting,
      columnFilters: filters,
    },
  })

  if (!isLoading && (!data || data.length === 0)) {
    return (
      <div className="p-4 flex gap-2">
        <div className="mt-3 text-center">No missing pathologies found</div>
      </div>
    )
  }

  return (
    <>
      <div className="flex gap-4 h-[75vh] p-4">
        {/* Left Panel - Body Part Filter and Pathologies Table */}
        <div className="w-1/4 flex flex-col">
          <div className="mb-4">
            <label className="text-xs font-semibold text-gray-500 uppercase mb-2 block">
              Body Part Filter
            </label>
            <div className="relative">
              <select
                className="appearance-none rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 text-sm leading-5 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 w-full"
                value={
                  filters.find((f) => f.id === "BodyPart")?.value || "spine"
                }
                onChange={(event) =>
                  ((value) => {
                    setFilters((prev) => {
                      const otherFilters = prev.filter(
                        (f) => f.id !== "BodyPart"
                      )
                      if (value === "All") {
                        return otherFilters
                      }
                      return [...otherFilters, { id: "BodyPart", value }]
                    })
                  })(event.target.value)
                }
              >
                {(distinctBodyParts
                  ? [].concat(distinctBodyParts.data)
                  : []
                ).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div className="absolute top-[50%] right-0 pr-2 translate-y-[-50%]">
                <FontAwesomeIcon
                  aria-hidden="true"
                  className="h-4 w-4 text-gray-400"
                  icon={faChevronDown}
                />
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-hidden">
            <div className="bg-white border border-gray-200 rounded-lg h-full flex flex-col">
              <div className="border-b border-gray-200 bg-gray-50 p-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-gray-700">
                    Pathology
                  </span>
                  <span className="text-sm font-semibold text-gray-700">
                    Count
                  </span>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader className="border-gray-500" />
                  </div>
                ) : (
                  <div>
                    {table.getRowModel().rows.map((row) => (
                      <div
                        key={row.id}
                        className={`flex justify-between items-center p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                          selectedLog &&
                          selectedLog.Pathology === row.original.Pathology &&
                          selectedLog.BodyPart === row.original.BodyPart
                            ? "bg-blue-50 border-l-4 border-l-blue-500"
                            : ""
                        }`}
                        onClick={() => {
                          setSelectedLog(row.original)
                        }}
                      >
                        <span className="text-sm text-gray-700 truncate flex-1 mr-2">
                          <div className="flex items-center gap-2">
                            <span>{row.original.Pathology || ""}</span>
                          </div>
                        </span>
                        <span className="text-sm font-semibold text-gray-900">
                          {row.original.count || 0}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Middle Panel - Recent Reports */}
        {selectedLog && (
          <div className="w-1/6 flex flex-col">
            <div className="mb-4">
              <h3 className="text-sm font-bold text-gray-600 uppercase">
                Recent Reports
              </h3>
              <p className="text-xs text-gray-400">
                <span className="italic font-semibold">
                  {selectedLog ? selectedLog.Pathology : ""}
                </span>{" "}
                cases
              </p>
            </div>
            <div className="flex-1 bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="h-full overflow-y-auto p-2">
                {isLoadingMissingPathologyLogs ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader className="border-gray-500" />
                  </div>
                ) : missingPathologyLogsData &&
                  missingPathologyLogsData.length > 0 ? (
                  <div className="space-y-2">
                    {missingPathologyLogsData.map((log) => (
                      <div
                        key={log.MissingPathologiesLogId}
                        className={`p-2 rounded cursor-pointer text-sm transition-colors ${
                          selectedMissingPathologyLogId ===
                          log.MissingPathologiesLogId
                            ? "bg-blue-100 text-blue-800 border border-blue-300"
                            : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                        }`}
                        onClick={() =>
                          setSelectedMissingPathologyLogId(
                            log.MissingPathologiesLogId
                          )
                        }
                      >
                        {log.AccessionNumber}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-500">
                    No reports found
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Right Panel - Report Details */}
        <div className="flex-1 flex flex-col">
          <div className="flex-1 flex flex-col gap-4">
            <div className="flex-1 bg-white border border-gray-200 rounded-lg">
              <div className="border-b border-gray-200 bg-gray-50 p-3 rounded-t-lg">
                <h4 className="text-sm font-bold text-gray-600 flex items-center">
                  Report Text
                </h4>
              </div>
              <div className="h-full overflow-y-auto p-4">
                <div className="text-sm text-gray-700 leading-relaxed">
                  {selectedMissingPathologyLogId ? (
                    isLoadingMissingPathologyLog ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader className="border-gray-500" />
                      </div>
                    ) : (
                      <div className="whitespace-pre-wrap max-h-[300px] overflow-y-auto">
                        {missingPathologyLog?.log?.ExtractedReportText ||
                          "No report text available"}
                      </div>
                    )
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      Select a report to view details
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex-1 bg-white border border-gray-200 rounded-lg">
              <div className="border-b border-gray-200 bg-gray-50 p-3 rounded-t-lg">
                <h4 className="text-sm font-bold text-gray-600 flex items-center">
                  AI Response
                </h4>
              </div>
              <div className="h-full p-4 max-h-[250px] overflow-y-auto">
                <div className="text-sm text-gray-700">
                  {selectedMissingPathologyLogId ? (
                    isLoadingMissingPathologyLog ? (
                      <div className="flex items-center justify-center h-full">
                        <Loader className="border-gray-500" />
                      </div>
                    ) : (
                      <pre className="whitespace-pre-wrap font-mono text-xs">
                        {(() => {
                          try {
                            const response =
                              missingPathologyLog?.log?.AIResponse
                            if (!response) return "No AI response available"
                            const parsed = JSON.parse(response)
                            return JSON.stringify(parsed, null, 2)
                          } catch (error) {
                            return (
                              missingPathologyLog?.log?.AIResponse ||
                              "No AI response available"
                            )
                          }
                        })()}
                      </pre>
                    )
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                      Select a report to view AI response
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default MissingPathologyStatsTable
